import { LogOut, UserRound, SlidersHorizontal, Heart } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAccount } from "@/lib/account";
import { goTo } from "@/lib/api";

const Initial = ({ user }) => (user?.picture
  ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" referrerPolicy="no-referrer" />
  : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#9E4429] font-serif text-sm text-[#FBF5EE]">{(user?.name ?? "L").slice(0, 1).toUpperCase()}</span>);

export const useSignOut = () => {
  const { signOut } = useAccount();
  return async () => { await signOut(); toast("Signed out", { description: "Your calendar was removed from this browser. It's safe in your account." }); };
};

export const useOpenSaved = () => {
  const { setKitchenView } = useAccount();
  return () => { setKitchenView("saved"); setTimeout(() => goTo("saved-recipes"), 50); };
};

// Desktop header control: "Sign in" or avatar menu
export const AccountMenu = () => {
  const { user, ready, openAuth, setProfileOpen } = useAccount();
  const out = useSignOut();
  const saved = useOpenSaved();
  if (!ready) return null;
  if (!user) {
    return <button onClick={() => openAuth("signin")} data-testid="nav-sign-in-button" className="hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-[#2C221E] transition-colors hover:text-[#9E4429] md:flex"><UserRound className="h-4 w-4" />Sign in</button>;
  }
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger data-testid="nav-account-menu-trigger" className="hidden rounded-full outline-none ring-[#9E4429] focus-visible:ring-2 md:block" aria-label="Your account"><Initial user={user} /></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 rounded-2xl border-[#C86D51]/20 bg-[#FDFBF7]">
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-semibold text-[#2C221E]" data-testid="nav-account-name">{user.name}</p>
          <p className="truncate text-xs text-[#6B5A52]" data-testid="nav-account-email">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="nav-profile-button" className="cursor-pointer text-[#2C221E]"><SlidersHorizontal className="mr-2 h-4 w-4" />My profile</DropdownMenuItem>
        <DropdownMenuItem onClick={saved} data-testid="nav-saved-recipes-button" className="cursor-pointer text-[#2C221E]"><Heart className="mr-2 h-4 w-4" />Saved recipes{Array.isArray(user.favorites) && user.favorites.length ? ` (${user.favorites.length})` : ""}</DropdownMenuItem>
        <DropdownMenuItem onClick={out} data-testid="nav-sign-out-button" className="cursor-pointer text-[#9E4429]"><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Phone menu entry
export const MobileAccountItem = ({ onDone }) => {
  const { user, ready, openAuth, setProfileOpen: openProfile } = useAccount();
  const out = useSignOut();
  const saved = useOpenSaved();
  if (!ready) return null;
  return (
    <div className="mt-2 border-t border-[#C86D51]/15 pt-3">
      {user ? (
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2.5"><Initial user={user} /><span className="truncate text-sm text-[#5C4A42]" data-testid="nav-mobile-account-email">{user.email}</span></span>
          <span className="flex shrink-0 gap-4">
            <button onClick={() => { onDone(); saved(); }} data-testid="nav-mobile-saved-button" className="text-sm font-semibold text-[#2C221E]">Saved</button>
            <button onClick={() => { onDone(); openProfile(true); }} data-testid="nav-mobile-profile-button" className="text-sm font-semibold text-[#2C221E]">Profile</button>
            <button onClick={() => { onDone(); out(); }} data-testid="nav-mobile-sign-out-button" className="text-sm font-semibold text-[#9E4429]">Sign out</button>
          </span>
        </div>
      ) : (
        <button onClick={() => { onDone(); openAuth("signin"); }} data-testid="nav-mobile-sign-in-button" className="flex w-full items-center gap-2 py-2 text-left font-serif text-2xl text-[#2C221E]"><UserRound className="h-5 w-5" />Sign in</button>
      )}
    </div>
  );
};
